import { useCallback, useEffect, useState } from 'react';
import { MapPin, Plus } from 'lucide-react';
import AppIcon from '../components/AppIcon';
import AddressCard from '../components/AddressCard';
import AddressForm from '../components/AddressForm';
import { getErrorMessage } from '../api/errors';
import { addressesService } from '../services';
import type { UserAddressInput } from '../types/api';
import type { UserAddress } from '../types';
import {
  MAX_USER_ADDRESSES,
  emptyAddressInput,
  userAddressInputFromUserAddress,
} from '../utils/address';

type EditorMode = 'create' | 'edit' | null;

export default function Addresses() {
  const [addresses, setAddresses] = useState<UserAddress[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editorMode, setEditorMode] = useState<EditorMode>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formValue, setFormValue] = useState<UserAddressInput>(emptyAddressInput());
  const [isSaving, setIsSaving] = useState(false);

  const loadAddresses = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await addressesService.fetchAddresses();
      setAddresses(data);
    } catch (err) {
      setError(getErrorMessage(err, 'Adresler yüklenemedi.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAddresses();
  }, [loadAddresses]);

  const openCreate = () => {
    setEditorMode('create');
    setEditingId(null);
    setFormValue(emptyAddressInput());
  };

  const openEdit = (address: UserAddress) => {
    setEditorMode('edit');
    setEditingId(address.id);
    setFormValue(userAddressInputFromUserAddress(address));
  };

  const closeEditor = () => {
    if (isSaving) return;
    setEditorMode(null);
    setEditingId(null);
    setFormValue(emptyAddressInput());
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    try {
      if (editorMode === 'edit' && editingId) {
        await addressesService.updateAddress(editingId, formValue);
      } else {
        await addressesService.createAddress(formValue);
      }
      closeEditor();
      await loadAddresses();
    } catch (err) {
      setError(getErrorMessage(err, 'Adres kaydedilemedi.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string, label: string) => {
    if (!confirm(`"${label}" adresini silmek istediğinize emin misiniz?`)) return;

    setBusyId(id);
    setError('');
    try {
      await addressesService.deleteAddress(id);
      await loadAddresses();
    } catch (err) {
      setError(getErrorMessage(err, 'Adres silinemedi.'));
    } finally {
      setBusyId(null);
    }
  };

  const handleSetDefaultShipping = async (id: string) => {
    setBusyId(id);
    setError('');
    try {
      await addressesService.setDefaultShipping(id);
      await loadAddresses();
    } catch (err) {
      setError(getErrorMessage(err, 'Varsayılan teslimat adresi güncellenemedi.'));
    } finally {
      setBusyId(null);
    }
  };

  const handleSetDefaultBilling = async (id: string) => {
    setBusyId(id);
    setError('');
    try {
      await addressesService.setDefaultBilling(id);
      await loadAddresses();
    } catch (err) {
      setError(getErrorMessage(err, 'Varsayılan fatura adresi güncellenemedi.'));
    } finally {
      setBusyId(null);
    }
  };

  const atLimit = addresses.length >= MAX_USER_ADDRESSES;

  return (
    <div className="container addresses-page">
      <div className="page-header addresses-page-header">
        <div>
          <h1>Adreslerim</h1>
          <p>Kayıtlı teslimat ve fatura adreslerinizi yönetin</p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          disabled={atLimit || editorMode === 'create'}
          onClick={openCreate}
        >
          <AppIcon icon={Plus} size={16} strokeWidth={2} />
          Yeni Adres Ekle
        </button>
      </div>

      {atLimit && (
        <p className="address-limit-note">En fazla {MAX_USER_ADDRESSES} adres kaydedebilirsiniz.</p>
      )}

      {error && (
        <div className="auth-error">
          {error}
          <button type="button" className="btn btn-outline btn-sm" onClick={() => void loadAddresses()}>
            Tekrar Dene
          </button>
        </div>
      )}

      {editorMode && (
        <section className="address-editor">
          <h2>{editorMode === 'edit' ? 'Adresi Düzenle' : 'Yeni Adres'}</h2>
          <AddressForm
            idPrefix={editorMode === 'edit' ? `edit-${editingId}` : 'create'}
            value={formValue}
            showDefaultFlags
            disabled={isSaving}
            onChange={setFormValue}
          />
          <div className="address-editor-actions">
            <button type="button" className="btn btn-outline btn-sm" disabled={isSaving} onClick={closeEditor}>
              Vazgeç
            </button>
            <button type="button" className="btn btn-primary btn-sm" disabled={isSaving} onClick={() => void handleSave()}>
              {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </section>
      )}

      {isLoading ? (
        <p>Adresleriniz yükleniyor...</p>
      ) : addresses.length === 0 ? (
        <div className="empty-cart">
          <span className="empty-icon">
            <AppIcon icon={MapPin} size={64} strokeWidth={1.5} />
          </span>
          <h2>Henüz kayıtlı adresiniz yok</h2>
          <p>Checkout sırasında veya buradan adres ekleyebilirsiniz.</p>
          {!editorMode && (
            <button type="button" className="btn btn-primary" onClick={openCreate}>
              İlk Adresi Ekle
            </button>
          )}
        </div>
      ) : (
        <div className="addresses-list">
          {addresses.map((address) => (
            <AddressCard
              key={address.id}
              address={address}
              busy={busyId === address.id || isSaving}
              onEdit={() => openEdit(address)}
              onDelete={() => void handleDelete(address.id, address.label)}
              onSetDefaultShipping={() => void handleSetDefaultShipping(address.id)}
              onSetDefaultBilling={() => void handleSetDefaultBilling(address.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
